import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { adminApi } from '../../services/api';
import { MessageCircle, X, Send, Sparkles, Loader2 } from 'lucide-react';

export default function AiAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    setMessages(m => [...m, { role: 'user', text }]);
    setLoading(true);

    try {
      const r = await adminApi.post('/admin/ai-assistant/chat', {
        message: text,
        session_id: sessionId,
      });
      setMessages(m => [...m, { role: 'assistant', text: r.data.response }]);
    } catch (e) {
      const errMsg = e.response?.data?.detail || 'Errore di comunicazione con l\'assistente';
      setMessages(m => [...m, { role: 'assistant', text: errMsg, error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating button */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
            whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }}
            onClick={() => setOpen(true)}
            data-testid="ai-assistant-toggle"
            className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-2xl bg-[#D44E20] text-white shadow-lg shadow-[#D44E20]/25 flex items-center justify-center hover:bg-[#B3421A] transition-colors"
          >
            <Sparkles size={22} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] h-[520px] bg-white rounded-2xl shadow-2xl border border-slate-200/60 flex flex-col overflow-hidden"
            data-testid="ai-assistant-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/80">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-[#D44E20]/10 flex items-center justify-center">
                  <Sparkles size={16} className="text-[#D44E20]" />
                </div>
                <div>
                  <p className="text-sm font-bold text-[#1A1A1A]">Assistente Lazo</p>
                  <p className="text-[10px] text-[#64748B]">Powered by AI</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="ai-assistant-close"
                className="p-1.5 rounded-lg text-[#94A3B8] hover:bg-slate-100 hover:text-[#64748B] transition-colors">
                <X size={16} />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="ai-assistant-messages">
              {messages.length === 0 && (
                <div className="text-center py-8">
                  <Sparkles size={28} className="mx-auto text-[#D44E20]/30 mb-3" />
                  <p className="text-sm font-medium text-[#1A1A1A]">Ciao! Come posso aiutarti?</p>
                  <p className="text-[11px] text-[#64748B] mt-1 max-w-[260px] mx-auto">
                    Chiedimi qualsiasi cosa su Lazo: come creare un coupon, strategie marketing, analisi dati...
                  </p>
                  <div className="mt-4 space-y-1.5">
                    {['Quanti clienti attivi ho?', 'Come creo un coupon?', 'Suggeriscimi una strategia per il weekend'].map(q => (
                      <button key={q} onClick={() => { setInput(q); setTimeout(handleSend, 100); }}
                        className="block w-full text-left px-3 py-2 rounded-lg text-[11px] text-[#64748B] bg-slate-50 hover:bg-slate-100 transition-colors">
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[12px] leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-[#D44E20] text-white rounded-br-md'
                      : msg.error
                        ? 'bg-red-50 text-red-700 border border-red-100 rounded-bl-md'
                        : 'bg-slate-100 text-[#1A1A1A] rounded-bl-md'
                  }`} style={{ whiteSpace: 'pre-wrap' }}>
                    {msg.text}
                  </div>
                </motion.div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin text-[#D44E20]" />
                    <span className="text-[11px] text-[#64748B]">Sto pensando...</span>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-slate-100 px-3 py-2.5 bg-white">
              <div className="flex items-center gap-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Scrivi un messaggio..."
                  rows={1}
                  data-testid="ai-assistant-input"
                  className="flex-1 resize-none bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm text-[#1A1A1A] placeholder:text-[#94A3B8] focus:border-[#D44E20] focus:outline-none max-h-20"
                />
                <button onClick={handleSend} disabled={!input.trim() || loading}
                  data-testid="ai-assistant-send"
                  className="w-9 h-9 rounded-xl bg-[#D44E20] text-white flex items-center justify-center hover:bg-[#B3421A] disabled:opacity-40 transition-all active:scale-95">
                  <Send size={14} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
